import {useEffect, useMemo} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {apptPatientSelect as strings} from "strings";

import Button from "components/Button";
import PageTitle from "components/PageTitle";
import Select from "components/Select";

import {useGetAppointmentCampaigns} from "liberty-core/api/react-query/appointment";

import {useAuthenticatedUser} from "lib/auth";
import {redirectToAppointments} from "lib/redirect";
import useCurrentPatientId from "lib/useCurrentPatientId";
import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import styles from "./AppointmentPatientSelectPage.module.css";

const DEFAULT_PATIENT_ID = "defaultPatientId";

export default function AppointmentPatientSelectPage() {
  const [redirectParams] = useSearchParams();
  const campaignId = redirectParams.get("campaignId");
  const optionId = redirectParams.get("optionId");
  const [authenticatedUser] = useAuthenticatedUser();
  const [pharmacy] = useCurrentPharmacy();
  const navigate = useNavigate();
  const {selectedPatientId, updateSelectedPatientId} = useCurrentPatientId(
    authenticatedUser?.data.linkedPatients ?? [],
  );
  const {data: apptCampaigns, isLoading: isLoadingApptCampaigns} =
    useGetAppointmentCampaigns(pharmacy?.id ?? 0, {enabled: !!pharmacy});

  const patientNameOptions = useMemo(() => {
    if (!authenticatedUser?.data.linkedPatients?.length) {
      return [];
    }

    return [
      {
        value: DEFAULT_PATIENT_ID,
        label: authenticatedUser.data.name,
      },
      ...authenticatedUser.data.linkedPatients.map((p) => ({
        value: p.patientId,
        label: `${p.firstName} ${p.lastName}`,
      })),
    ].sort((a, b) => {
      if (a.label < b.label) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });
  }, [authenticatedUser]);

  useEffect(() => {
    if (redirectParams && !campaignId) {
      navigate("/appointments", {replace: true});
    }
  }, [redirectParams, campaignId, navigate]);

  return (
    <div>
      <PageTitle>{strings.title}</PageTitle>
      <div className={styles.content}>
        <div>{strings.description}</div>
        <Select
          className={styles.patientNameSelect}
          value={selectedPatientId ?? DEFAULT_PATIENT_ID}
          options={patientNameOptions}
          disabled={!authenticatedUser?.data.linkedPatients?.length}
          onChange={(v) =>
            updateSelectedPatientId(v === DEFAULT_PATIENT_ID ? null : v)
          }
        />
        <Button
          onClick={() => {
            redirectToAppointments(
              navigate,
              campaignId,
              optionId,
              null,
              selectedPatientId ?? authenticatedUser?.data.patientId ?? "",
              apptCampaigns ?? [],
            );
          }}
          loading={isLoadingApptCampaigns}
        >
          {strings.next}
        </Button>
      </div>
    </div>
  );
}
